import { useEffect, useState } from "react";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { SnackbarKey, useSnackbar } from "notistack";
import {
  Box,
  Breadcrumbs,
  Grid,
  IconButton,
  Link,
  Tab,
  Tabs,
  TabsProps,
  Typography,
} from "@mui/material";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { Close as CloseIcon, Home as HomeIcon } from "@mui/icons-material";
import { useCanGet } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Get";
import { useCanListDiscoveryResults } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/ListDiscoveryResults";
import { useCanGetTokenForIntegration } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/GetTokenForIntegration";
import { useCanAssign } from "~/lib/iam/actions/cnspec/policy/PolicyResolver/Assign";
import { AwsIntegrationServerlessIndividualAccountsTab } from "~/pages/integrations/aws/AwsIntegrationServerlessPage/components/AwsIntegrationServerlessIndividualAccountsTab";
import { AWSOverviewAccountTab } from "~/components/aws-overview-account-tab";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationQuery,
  GetIntegrationsSummaryDocument,
  useDeleteClientIntegrationMutation,
  useGetClientIntegrationQuery,
  useGetClientIntegrationTokenQuery,
  useListDiscoveryResultsQuery,
  usePingIntegrationLazyQuery,
  useTriggerActionLazyQuery,
} from "~/operations";
import { AwsIntegrationServerlessHeader } from "./components/AwsIntegrationServerlessHeader";
import { AwsIntegrationServerlessAlert } from "~/pages/integrations/aws/AwsIntegrationServerlessPage/components/AwsIntegrationServerlessAlert/AwsIntegrationServerlessAlert";
import { IntegrationRecommendedPolicies } from "../../components/IntegrationRecommendedPolicies";
import { AwsIntegrationServerlessDeleteConfirmDialog } from "./components/AwsIntegrationServerlessDeleteConfirmDialog";
import { AwsIntegrationAssetsButton } from "../components/AwsIntegrationAssetsButton";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { SpacePagesIntegrations } from "~/pages/integrations/utils/integrationRoutesUtils";

export type ClientIntegration = GetClientIntegrationQuery["clientIntegration"];
export type Integration = ClientIntegration["integration"];
export type ConfigurationOptions = NonNullable<
  Integration["configurationOptions"]
>;
export type AwsConfigurationOptions = Extract<
  ConfigurationOptions,
  { __typename: "AWSConfigurationOptions" }
>;
export type AwsIntegration = Integration & {
  configurationOptions: AwsConfigurationOptions;
  token: string | undefined;
};

export type AWSIntegrationStatus =
  | "ACTIVE"
  | "CONFIGURING"
  | "REGISTERING"
  | "WAITING_FOR_SETUP"
  | "SETUP_IN_PROGRESS"
  | "NOT_READY"
  | "ERROR"
  | "MISSING"
  | "DELETED"
  | "WARNING";

type AwsIntegrationServerlessPageState = {
  currentTab: string;
  agent: any | null;
  openDeleteConfirmDialog: boolean;
  scanConfigForm: any;
  isDeleting: boolean;
  lastUpdate: number;
};

interface AwsIntegrationServerlessPageProps {
  scope?: SpaceScope | OrganizationScope;
}

export function AwsIntegrationServerlessPage(
  props: AwsIntegrationServerlessPageProps,
) {
  const navigate = useNavigate();
  const { integrationId } = useParams();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [_, _setSearchParams] = useSearchParams();

  const defaultTab = "overview";
  const tab = window.location.pathname.split("/").pop() || defaultTab;
  const currentTab = [
    "overview",
    "individual-accounts",
    "policies",
    "configuration",
  ].includes(tab)
    ? tab
    : defaultTab;

  const [state, setState] = useState<AwsIntegrationServerlessPageState>({
    currentTab,
    agent: null,
    openDeleteConfirmDialog: false,
    scanConfigForm: undefined,
    isDeleting: false,
    lastUpdate: Date.now(),
  });

  function mergeState(params: any) {
    return setState((prevState) => ({
      ...prevState,
      ...params,
    }));
  }

  const { scope } = props;
  const mrn = `//integration.api.mondoo.app/spaces/${scope?.id}/aws/${integrationId}`;

  const hasIntegrationsGetPermission = useCanGet();
  const hasIntegrationsDiscoveryPermission = useCanListDiscoveryResults();
  const hasIntegrationsTokenPermission = useCanGetTokenForIntegration();
  const hasPolicyAssignPermission = useCanAssign();

  const clientIntegrationResult = useGetClientIntegrationQuery({
    variables: { mrn },
    pollInterval: 10000,
    fetchPolicy: "network-only",
    skip: !hasIntegrationsGetPermission,
  });

  const listDiscoveryResultsResult = useListDiscoveryResultsQuery({
    variables: { input: { mrn } },
    pollInterval: 10000,
    fetchPolicy: "network-only",
    skip: !hasIntegrationsDiscoveryPermission,
  });

  const clientIntegrationTokenResult = useGetClientIntegrationTokenQuery({
    variables: { input: { mrn, longLivedToken: false } },
    skip: !hasIntegrationsTokenPermission,
  });

  const [pingClientIntegration] = usePingIntegrationLazyQuery({
    variables: { input: { mrn } },
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    variables: { input: { mrn, type: ActionType.RunScan } },
    fetchPolicy: "no-cache",
  });

  const [triggerForceLambdaUpdate] = useTriggerActionLazyQuery({
    variables: { input: { mrn, type: ActionType.Update } },
  });
  const [triggerSendLambdaMetrics] = useTriggerActionLazyQuery({
    variables: { input: { mrn, type: ActionType.Metrics } },
  });
  const [triggerSendDiagnosticsData] = useTriggerActionLazyQuery({
    variables: { input: { mrn, type: ActionType.Diagnostics } },
  });
  const [triggerCancelScans] = useTriggerActionLazyQuery({
    variables: { input: { mrn, type: ActionType.ClearScanQueue } },
  });

  const [deleteClientIntegration] = useDeleteClientIntegrationMutation({
    refetchQueries: [GetIntegrationsSummaryDocument],
  });

  useEffect(() => {
    const integration =
      clientIntegrationResult.data?.clientIntegration.integration;
    if (
      integration?.configurationOptions?.__typename !==
      "AWSConfigurationOptions"
    ) {
      return;
    }
    const { scanConfiguration } = integration.configurationOptions;
    const { ec2ScanOptions, eventScanTriggers } = scanConfiguration;
    mergeState({
      scanConfigForm: {
        region: integration.configurationOptions.region,
        ...scanConfiguration,
        ec2ScanOptions: {
          ...ec2ScanOptions,
          instanceIdsFilter: ec2ScanOptions.instanceIdsFilter?.join(", ") || "",
          regionsFilter: ec2ScanOptions.regionsFilter?.join(", ") || "",
          tagsFilter: JSON.stringify(ec2ScanOptions.tagsFilter),
          ebsScanOptions: {
            ...ec2ScanOptions.ebsScanOptions,
          },
        },
        eventScanTriggersString: eventScanTriggers?.map((trigger: any) => {
          const { scanType, eventSource, eventDetailType } = trigger;
          return [scanType.toLowerCase(), eventSource, eventDetailType].join(
            ":",
          );
        }),
      },
    });
  }, [clientIntegrationResult.data]);

  const integration =
    clientIntegrationResult.data?.clientIntegration.integration;

  setDocumentTitle([integration?.name, "AWS", "Integrations", scope?.name]);

  if (
    clientIntegrationResult.loading ||
    listDiscoveryResultsResult.loading ||
    clientIntegrationTokenResult.loading
  ) {
    return <LoadingPage what="Integration" />;
  }

  const token =
    clientIntegrationTokenResult.data?.getClientIntegrationToken.token;
  const discoveryResults =
    listDiscoveryResultsResult.data?.listDiscoveryResults;

  const { scanConfigForm } = state;

  if (
    !scope ||
    !integrationId ||
    !integration ||
    !scanConfigForm ||
    mrn !== integration.mrn ||
    integration.type !== ClientIntegrationType.Aws ||
    integration.configurationOptions?.__typename !== "AWSConfigurationOptions"
  ) {
    return <LoadingFailedPage what="Integration" />;
  }

  const awsIntegration = { ...integration, token } as AwsIntegration;
  const configurationOptions = awsIntegration.configurationOptions;

  const discoveredAssets =
    discoveryResults?.stats
      ?.flatMap((asset) => (asset ? [asset] : []))
      .sort((a, b) => {
        if (a.title < b.title) return -1;
        if (a.title > b.title) return 1;
        return 0;
      }) || [];

  const deleteIntegrationBegin = () => {
    mergeState({ openDeleteConfirmDialog: true });
  };

  const handleDeleteAgentClose = () => {
    mergeState({ isDeleting: false, openDeleteConfirmDialog: false });
  };

  const handleDeleteAgentConfirm = (): void => {
    mergeState({ isDeleting: true });

    const onRemoveSuccess = () => {
      const region = configurationOptions?.region;
      const url = `https://${region}.console.aws.amazon.com/cloudformation/home?region=${region}#/stacks`;
      const msg = (
        <span>
          Successfully removed AWS account. The AWS CloudFormation stack is
          being deleted.
          <br />
          <a
            style={{ textDecoration: "underline" }}
            href={url}
            target="_blank"
            rel="noreferrer noopener"
          >
            Navigate to the AWS Console
          </a>{" "}
          to confirm that the deletion completed.
        </span>
      );
      const action = (key: SnackbarKey) => (
        <IconButton
          size="small"
          aria-label="close"
          onClick={() => closeSnackbar(key)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      );

      // Display success msg
      enqueueSnackbar(msg, {
        persist: true,
        variant: "success",
        action,
      });

      // Nav back to list view
      navigate(`/space/${SpacePagesIntegrations}/aws?${scope.params}`);
    };

    const onRemoveError = () => {
      mergeState({ isDeleting: false });
      const msg = "Failed to remove AWS account.";
      enqueueSnackbar(msg, { variant: "error" });
    };

    Promise.all(
      [awsIntegration].map((item) =>
        deleteClientIntegration({ variables: { input: { mrn: item.mrn } } }),
      ),
    )
      .then(onRemoveSuccess)
      .catch(onRemoveError);
  };

  const tabs = ["overview"];
  const tabNames = ["Overview"];

  if (configurationOptions?.isOrganization) {
    tabs.push("individual-accounts");
    tabNames.push("Individual Accounts");
  }

  if (hasPolicyAssignPermission) {
    tabs.push("policies");
    tabNames.push("Recommended Policies");
  }

  const handleTabChange: TabsProps["onChange"] = (e, value) => {
    const currentTab = tabs[value];
    const newUrl = `/space/${SpacePagesIntegrations}/aws/${integrationId}/${currentTab}?${scope.params}`;
    navigate(newUrl);
  };

  const triggerScan = () => {
    const onTriggerSuccess = () => {
      const msg = "Successfully triggered scan.";
      // Display success msg
      enqueueSnackbar(msg, { variant: "success" });
    };

    const onTriggerError = () => {
      const msg = "Failed to trigger scan.";
      // Display error msg
      enqueueSnackbar(msg, { variant: "error" });
    };

    triggerClientIntegrationScan().then(onTriggerSuccess).catch(onTriggerError);
  };

  const ping = () => {
    const onPingSuccess = () => {
      const msg = "Successfully pinged AWS.";
      // Display success msg
      enqueueSnackbar(msg, { variant: "success" });
    };

    const onPingError = () => {
      const msg = "Failed to ping AWS.";
      // Display error msg
      enqueueSnackbar(msg, { variant: "error" });
    };

    pingClientIntegration().then(onPingSuccess).catch(onPingError);
  };

  const forceLambdaUpdate = () => {
    const onForceLambdaUpdateSuccess = () => {
      const msg = "Successfully triggered lambda update.";
      // Display success msg
      enqueueSnackbar(msg, { variant: "success" });
    };

    const onForceLambdaUpdateError = () => {
      const msg = "Failed to trigger lambda update";
      // Display error msg
      enqueueSnackbar(msg, { variant: "error" });
    };

    triggerForceLambdaUpdate()
      .then(onForceLambdaUpdateSuccess)
      .catch(onForceLambdaUpdateError);
  };

  const sendLambdaMetrics = () => {
    const onSendLambdaMetricsSuccess = () => {
      const msg = "Successfully sent lambda metrics.";
      // Display success msg
      enqueueSnackbar(msg, { variant: "success" });
    };

    const onSendLambdaMetricsError = () => {
      const msg = "Failed to send lambda metrics.";
      // Display error msg
      enqueueSnackbar(msg, { variant: "error" });
    };

    triggerSendLambdaMetrics()
      .then(onSendLambdaMetricsSuccess)
      .catch(onSendLambdaMetricsError);
  };

  const sendDiagnosticsData = () => {
    const onSendDiagnosticsDataSuccess = () => {
      const msg = "Successfully sent diagnostics data.";
      // Display success msg
      enqueueSnackbar(msg, { variant: "success" });
    };

    const onSendDiagnosticsDataError = () => {
      const msg = "Failed to send diagnostics data.";
      // Display error msg
      enqueueSnackbar(msg, { variant: "error" });
    };

    triggerSendDiagnosticsData()
      .then(onSendDiagnosticsDataSuccess)
      .catch(onSendDiagnosticsDataError);
  };

  const cancelScans = () => {
    const onCancelScansDataSuccess = () => {
      const msg = "Successfully sent cancel scans request.";
      // Display success msg
      enqueueSnackbar(msg, { variant: "success" });
    };

    const onCancelScansDataError = () => {
      const msg = "Failed to send cancel scans request.";
      // Display error msg
      enqueueSnackbar(msg, { variant: "error" });
    };

    triggerCancelScans()
      .then(onCancelScansDataSuccess)
      .catch(onCancelScansDataError);
  };

  const cloudFormationStackUrl = `https://${configurationOptions?.region}.console.aws.amazon.com/cloudformation/home?region=${configurationOptions?.region}#/stacks`;

  let content = null;
  switch (currentTab) {
    case "overview":
      content = (
        <AWSOverviewAccountTab
          {...{
            scope,
            integration: awsIntegration,
            integrationMrn: awsIntegration.mrn,
            discoveredAssets,
            integrationStatus: awsIntegration.status as AWSIntegrationStatus,
            token,
          }}
        />
      );
      break;
    case "individual-accounts":
      content = (
        <AwsIntegrationServerlessIndividualAccountsTab
          {...{
            awsIntegration,
            discoveredAssets,
            integrationStatus: awsIntegration.status as AWSIntegrationStatus,
          }}
        />
      );
      break;
    case "policies":
      content = (
        <IntegrationRecommendedPolicies {...{ scope }} filterTypes={["aws"]} />
      );
      break;
  }

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={`/space/integrations?${scope.params}`}
    >
      Integrations
    </Link>,
    <Link
      key="/space/integrations/aws"
      component={RouterLink}
      to={`/space/integrations/aws?${scope.params}`}
    >
      AWS
    </Link>,
    <Typography key={1}>{awsIntegration.name}</Typography>,
  ];

  return (
    <Grid container>
      <Breadcrumbs sx={{ mb: 3 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      <Grid size={12}>
        <AwsIntegrationServerlessAlert {...{ awsIntegration }} />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 12,
        }}
      >
        <AwsIntegrationServerlessHeader
          actions={{
            triggerScan,
            ping,
            forceLambdaUpdate,
            sendLambdaMetrics,
            sendDiagnosticsData,
            cancelScans,
            deleteIntegration: deleteIntegrationBegin,
          }}
          {...{
            mrn,
            scope,
            awsIntegration,
            integrationId,
          }}
        />
      </Grid>
      <Grid mt={4} mb={3} size={12}>
        <Box display="flex" justifyContent="flex-end">
          <AwsIntegrationAssetsButton
            integrationMrn={integration.mrn}
            show={Boolean(discoveredAssets.length)}
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            mb: 0,
            "&:after": {
              content: "''",
              position: "absolute",
              bottom: 0,
              left: 0,
              width: 1,
              height: 4,
              backgroundColor: "background.light",
            },
          }}
        >
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            value={tabs.indexOf(currentTab)}
            onChange={handleTabChange}
            sx={[
              {
                ".MuiTabs-indicator": {
                  height: 4,
                  zIndex: 1,
                },
              },
            ]}
          >
            {tabs.map((tab, i) => {
              return (
                <Tab
                  key={i}
                  label={tabNames[i]}
                  data-name={`integration-aws-tab-${tabNames[i]}`}
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: "text.secondary",
                    textTransform: "capitalize",
                  }}
                />
              );
            })}
          </Tabs>
        </Box>
      </Grid>
      <Grid size={12}>{content}</Grid>
      <AwsIntegrationServerlessDeleteConfirmDialog
        isDeleting={state.isDeleting}
        open={state.openDeleteConfirmDialog}
        onConfirm={handleDeleteAgentConfirm}
        onClose={handleDeleteAgentClose}
        integrationName={awsIntegration.name}
        stackUrl={cloudFormationStackUrl}
      />
    </Grid>
  );
}
